import parserUtils from '../../parser/utils/SlideParserUtils';
import shapeTypes from '../../constants/ShapeConstants';
import uuid from 'uuid';
import { updateCurrentSlideThumbnail, selectShape, createShape } from '../../actions/CurrentSlideActions';
import configuration from '../../constants/Configuration';
import properties from '../../constants/Properties';
import drawingUtils from '../../components/utils/DrawingUtils';
import saveManager from './SaveManager';
import audioEditorUtils from '../audioEditor/utils/AudioEditorUtils';
import thumbnailGenerator from './ThumbnailGenerator';
import { changeContextMode, hideMenus, setContextTarget } from '../../actions/MenuActions';
import modeType from '../../constants/ModeConstants';
import menuType from '../../constants/MenuType';
import storeManager from '../../store/StoreManager';
import textEditorUtils from '../menus/textEditorTool/utils/TextEditorUtils';

const createShapes = {
    useBackgroundAsImage: false,
    useFrameAsImage: false,
    useImageAsBackground: false,
    useVideoAsBackground: true,

    updateThumbnail (dispatch) {
        dispatch(updateCurrentSlideThumbnail(true));
    },
    createLine (dispatch, path, drawingProperties) {
        let shapeModel = {};
        const globalPathPoints = parserUtils.dataToPoints(path);

        if (!(globalPathPoints.length > 1 && globalPathPoints[0].x && globalPathPoints[0].y && globalPathPoints[1].x && globalPathPoints[1].y))
            return;

        shapeModel.type = shapeTypes.LINE;
        shapeModel.x1 = globalPathPoints[0].x;
        shapeModel.y1 = globalPathPoints[0].y;
        shapeModel.x2 = globalPathPoints[1].x;
        shapeModel.y2 = globalPathPoints[1].y;

        this.addCommonProperties(shapeModel);
        this.addStrokeProperties(shapeModel);

        if (drawingProperties) {
            shapeModel.strokeWidth = drawingProperties.strokeWidth;
            shapeModel.stroke = drawingProperties.strokeColor;
        } else {
            shapeModel.strokeWidth = 3;
        }

        dispatch(createShape(shapeModel));
        this.updateThumbnail(dispatch);
        audioEditorUtils.addActionToHistory();
        saveManager.isContentChanged(true);

        if (!drawingProperties)
            dispatch(selectShape(shapeModel.uuid));
        else
            drawingUtils.addCurrentShape(shapeModel);
    },
    createFreehand (dispatch, path, drawingProperties) {
        const globalPathPoints = parserUtils.dataToPoints(path);

        if (globalPathPoints.length < 10)
            return;

        let shapeModel = {};

        shapeModel.type = shapeTypes.FREEHAND;

        this.addCommonProperties(shapeModel);
        this.addFillProperties(shapeModel);
        this.addStrokeProperties(shapeModel);

        shapeModel.horizontalFlip = 1;
        shapeModel.verticalFlip = 1;

        const properties = parserUtils.getFreehandSize(globalPathPoints);
        const pathPoints = parserUtils.converToLocalPathPoints(globalPathPoints, properties);

        shapeModel.x = properties.x;
        shapeModel.y = properties.y;
        shapeModel.width = properties.width;
        shapeModel.height = properties.height;
        shapeModel.isNew = true;
        shapeModel.pathPoints = pathPoints;

        if (drawingProperties) {
            shapeModel.strokeWidth = drawingProperties.strokeWidth;
            shapeModel.stroke = drawingProperties.strokeColor;
        } else {
            shapeModel.strokeWidth = 3;
        }

        dispatch(createShape(shapeModel));
        this.updateThumbnail(dispatch);
        audioEditorUtils.addActionToHistory();
        saveManager.isContentChanged(true);

        if (!drawingProperties)
            dispatch(selectShape(shapeModel.uuid));
        else
            drawingUtils.addCurrentShape(shapeModel);
    },
    handleRectangularShape(shapeModel, topLeftPosition, dispatch) {
        this.addCommonProperties(shapeModel);
        this.addFillProperties(shapeModel);
        this.addStrokeProperties(shapeModel);
        this.addSize(shapeModel, false, topLeftPosition);

        dispatch(createShape(shapeModel));
        this.updateThumbnail(dispatch);
        audioEditorUtils.addActionToHistory();
        dispatch(selectShape(shapeModel.uuid));
        saveManager.isContentChanged(true);
    },
    createRectangle(dispatch, topLeftPosition) {
        let shapeModel = {};

        shapeModel.type = shapeTypes.RECTANGLE;
        this.handleRectangularShape(shapeModel, topLeftPosition, dispatch);
    },
    createTriangle(dispatch, topLeftPosition) {
        let shapeModel = {};

        shapeModel.type = shapeTypes.TRIANGLE;
        this.handleRectangularShape(shapeModel, topLeftPosition, dispatch);
    },
    createStar(dispatch, topLeftPosition) {
        let shapeModel = {};

        shapeModel.type = shapeTypes.STAR;
        this.handleRectangularShape(shapeModel, topLeftPosition, dispatch);
    },
    createHeart(dispatch, topLeftPosition) {
        let shapeModel = {};

        shapeModel.type = shapeTypes.HEART;
        this.handleRectangularShape(shapeModel, topLeftPosition, dispatch);
    },
    createEllipse (dispatch, topLeftPosition) {
        const size = properties.SHAPE_SIZE;
        let shapeModel = {};

        shapeModel.type = shapeTypes.ELLIPSE;

        this.addCommonProperties(shapeModel);
        this.addFillProperties(shapeModel);
        this.addStrokeProperties(shapeModel);
        this.addSize(shapeModel, false, topLeftPosition);

        dispatch(createShape(shapeModel));
        this.updateThumbnail(dispatch);
        audioEditorUtils.addActionToHistory();
        dispatch(selectShape(shapeModel.uuid));
        saveManager.isContentChanged(true);
    },
    createCurve (dispatch, topLeftPosition) {
        const size = properties.SHAPE_SIZE;
        let shapeModel = {};

        shapeModel.type = shapeTypes.CURVE;

        if (topLeftPosition) {
            shapeModel.x1 = topLeftPosition.x;
            shapeModel.y1 = topLeftPosition.y;
            shapeModel.x2 = topLeftPosition.x + size;
            shapeModel.y2 = topLeftPosition.y + size;
            shapeModel.cx = topLeftPosition.x + size;
            shapeModel.cy = topLeftPosition.y;
        } else {
            shapeModel.x1 = configuration.CANVAS_WIDTH / 2 - size / 2;
            shapeModel.y1 = configuration.CANVAS_HEIGHT / 2 - size / 2;
            shapeModel.x2 = configuration.CANVAS_WIDTH / 2 + size / 2;
            shapeModel.y2 = configuration.CANVAS_HEIGHT / 2 + size / 2;
            shapeModel.cx = configuration.CANVAS_WIDTH / 2 + size / 2;
            shapeModel.cy = configuration.CANVAS_HEIGHT / 2 - size / 2;
        }

        this.addStrokeProperties(shapeModel);
        this.addCommonProperties(shapeModel);

        shapeModel.strokeWidth = 3;

        dispatch(createShape(shapeModel));
        this.updateThumbnail(dispatch);
        audioEditorUtils.addActionToHistory();
        dispatch(selectShape(shapeModel.uuid));
        saveManager.isContentChanged(true);
    },
    addTextProperties (shapeModel) {
        if (!shapeModel.isRichText) {
            shapeModel.fontFamily = textEditorUtils.currentFont;
            shapeModel.fontAlign = 'left';
            shapeModel.fontBold = false;
            shapeModel.fontItalic = false;
            shapeModel.fontUnderline = false;
        }

        shapeModel.fontSize = 24;
        shapeModel.fontColor = '#000000';
        shapeModel.htmlText = '';
        shapeModel.backgroundVisible = true;
    },
    addStrokeProperties (shapeModel, opacity) {
        shapeModel.stroke = '#000000';
        shapeModel.strokeWidth = 1;
        if (opacity == 0)
            shapeModel.strokeOpacity = 0;
        else
            shapeModel.strokeOpacity = 1;
    },
    addFillProperties (shapeModel, opacity) {
        shapeModel.fill = '#ffffff';

        if (opacity == 0)
            shapeModel.fillOpacity = 0;
        else
            shapeModel.fillOpacity = 1;
    },
    addCommonProperties (shapeModel) {
        shapeModel.opacity = 1;
        shapeModel.isSelected = false;
        shapeModel.isLocked = false;
        shapeModel.rotation = 0;
        shapeModel.filter = '';
        shapeModel.uuid = uuid.v4();
    },
    addSize (shapeModel, isText = false, topLeftPosition = undefined) {
        const size = properties.SHAPE_SIZE;

        if (isText == true) {
            if (shapeModel.isRichText) {
                shapeModel.width = properties.RICH_TEXT_WIDTH;
                shapeModel.height = properties.RICH_TEXT_HEIGHT;
            } else {
                shapeModel.width = properties.TEXT_WIDTH;
                shapeModel.height = properties.TEXT_HEIGHT;
            }
        }
        else {
            shapeModel.width = size;
            shapeModel.height = size;
        }

        shapeModel.x = configuration.CANVAS_WIDTH / 2 - shapeModel.width / 2;
        shapeModel.y = configuration.CANVAS_HEIGHT / 2 - shapeModel.height / 2;

        if (topLeftPosition) {
            shapeModel.x = topLeftPosition.x;
            shapeModel.y = topLeftPosition.y;
        } else
            if (isText && shapeModel.isRichText)
                this.checkTopLeftPosition(shapeModel);
    },
    checkTopLeftPosition(shapeModel) {
        var offset = 10;
        var shapes = storeManager.getState().currentSlide.model.content;
        var i;
        var textShapes = [];
        var possiblePositions = [{x: shapeModel.x, y: shapeModel.y}];
        var count = 0;

        for (i=1; i<shapes.length; i++) {
            if (shapes[i].type == shapeTypes.TEXTBOX && shapes[i].isRichText) {
                textShapes.push(shapes[i]);
                count = count + 1;
                possiblePositions.push({x: shapeModel.x + count * offset, y: shapeModel.y + count * offset});
            }
        }

        for (i=0; i<possiblePositions.length; i++) {
            var position = possiblePositions[i];
            var isFree = true;

            for (var j=0; j<textShapes.length; j++) {
                if (position.x == textShapes[j].x && position.y == textShapes[j].y)
                    isFree = false;
            }

            if (isFree) {
                shapeModel.x = position.x;
                shapeModel.y = position.y;
                break;
            }
        }
    },
    addText(shapeModel, topLeftPosition) {
        this.addCommonProperties(shapeModel);

        this.addFillProperties(shapeModel);
        this.addStrokeProperties(shapeModel);

        this.addTextProperties(shapeModel);
        this.addSize(shapeModel, true, topLeftPosition);
        this.addControlPointPosition(shapeModel);
    },
    addControlPointPosition (shapeModel) {
        if (shapeModel.type != shapeTypes.TEXTBOX && shapeModel.type != shapeTypes.TEXTBOXSIMPLE)
        {
            if (shapeModel.type == shapeTypes.COMPLEX_SPEAK_BUBBLE) {
                shapeModel.cx = shapeModel.x;
                shapeModel.cy = shapeModel.y + 2.6 * shapeModel.height;

                shapeModel.cx1 = shapeModel.x + shapeModel.width;
                shapeModel.cy1 = shapeModel.y + 2.6 * shapeModel.height;

                if (shapeModel.isMindMap) {
                    shapeModel.cx2 = shapeModel.x;
                    shapeModel.cy2 = shapeModel.y - 1.6 * shapeModel.height;

                    shapeModel.cx3 = shapeModel.x + shapeModel.width;
                    shapeModel.cy3 = shapeModel.y - 1.6 * shapeModel.height;
                }
            } else {
                shapeModel.cx = shapeModel.x + 0.3 * shapeModel.width;
                shapeModel.cy = shapeModel.y + 1.6 * shapeModel.height;
            }
        }
    },
    createTextBox(dispatch, topLeftPosition, isRichText = false, mindMapProperty = undefined) {
        let shapeModel = {};
        shapeModel.type = shapeTypes.TEXTBOX;
        shapeModel.isRichText = isRichText;
        shapeModel.isNew = true;

        if (mindMapProperty)
            shapeModel.mindMapProperty = mindMapProperty;

        this.addText(shapeModel, topLeftPosition);

        dispatch(createShape(shapeModel));
        this.updateThumbnail(dispatch);
        audioEditorUtils.addActionToHistory();
        dispatch(selectShape(shapeModel.uuid, true));
        saveManager.isContentChanged(true);

        this.updateFontMenu(dispatch, shapeModel);
    },
    createTextBoxSimple(dispatch, topLeftPosition, isRichText = false) {
        let shapeModel = {};
        shapeModel.type = shapeTypes.TEXTBOXSIMPLE;
        shapeModel.isRichText = isRichText;
        shapeModel.isNew = true;
        this.addText(shapeModel, topLeftPosition);

        dispatch(createShape(shapeModel));
        this.updateThumbnail(dispatch);
        audioEditorUtils.addActionToHistory();
        dispatch(selectShape(shapeModel.uuid, true));
        saveManager.isContentChanged(true);

        this.updateFontMenu(dispatch, shapeModel);
    },
    createSpeachBubble(dispatch, topLeftPosition, isRichText = false) {
        let shapeModel = {};
        shapeModel.type = shapeTypes.SPEAK_BUBBLE;
        shapeModel.isRichText = isRichText;
        shapeModel.isNew = true;
        this.addText(shapeModel, topLeftPosition);

        dispatch(createShape(shapeModel));
        this.updateThumbnail(dispatch);
        audioEditorUtils.addActionToHistory();
        dispatch(selectShape(shapeModel.uuid, true));
        saveManager.isContentChanged(true);

        this.updateFontMenu(dispatch, shapeModel);
    },
    createComplexSpeachBubble(dispatch, topLeftPosition, isRichText = false, isMindMap = false) {
        let shapeModel = {};
        shapeModel.type = shapeTypes.COMPLEX_SPEAK_BUBBLE;
        shapeModel.isRichText = isRichText;
        shapeModel.isMindMap = isMindMap;
        shapeModel.isNew = true;
        this.addText(shapeModel, topLeftPosition);

        dispatch(createShape(shapeModel));
        this.updateThumbnail(dispatch);
        audioEditorUtils.addActionToHistory();
        dispatch(selectShape(shapeModel.uuid, true));
        saveManager.isContentChanged(true);

        this.updateFontMenu(dispatch, shapeModel);
    },
    createMindMapShape(dispatch, topLeftPosition, isRichText = false) {
        this.createComplexSpeachBubble(dispatch, topLeftPosition, isRichText, true);
    },
    createScreamBubble(dispatch, topLeftPosition, isRichText = false) {
        let shapeModel = {};
        shapeModel.type = shapeTypes.SCREAM_BUBBLE;
        shapeModel.isRichText = isRichText;
        shapeModel.isNew = true;
        this.addText(shapeModel, topLeftPosition);

        dispatch(createShape(shapeModel));
        this.updateThumbnail(dispatch);
        audioEditorUtils.addActionToHistory();
        dispatch(selectShape(shapeModel.uuid, true));
        saveManager.isContentChanged(true);

        this.updateFontMenu(dispatch, shapeModel);
    },
    createThoughtBubble(dispatch, topLeftPosition, isRichText = false) {
        let shapeModel = {};
        shapeModel.type = shapeTypes.THOUGHT_BUBBLE;
        shapeModel.isRichText = isRichText;
        shapeModel.isNew = true;
        this.addText(shapeModel, topLeftPosition);

        dispatch(createShape(shapeModel));
        this.updateThumbnail(dispatch);
        audioEditorUtils.addActionToHistory();
        dispatch(selectShape(shapeModel.uuid, true));
        saveManager.isContentChanged(true);

        this.updateFontMenu(dispatch, shapeModel);
    },
    updateFontMenu(dispatch, shapeModel) {
        dispatch(setContextTarget(shapeModel));
        dispatch(hideMenus([menuType.SHAPE_MENU]));

        setTimeout(function() {
            if (!shapeModel.isRichText)
                dispatch(changeContextMode(modeType.OBJECT_TEXT_STYLING));
        }.bind(this), 20);
    },
    checkFontMenu(dispatch, shapeModel) {
        if (storeManager.getState().menu.contextMode == modeType.ANIMATION_CONTEXT)
            return;

        this.updateFontMenu(dispatch, shapeModel);
    },
    createImage(dispatch, item, topLeftPosition, mindMapProperty) {
        let shapeModel = {};

        this.dragDropTopLeftPosition = topLeftPosition;
        shapeModel.type = shapeTypes.IMAGE;
        shapeModel.isNewImage = true;

        if (mindMapProperty)
            shapeModel.mindMapProperty = mindMapProperty;

        this.addCommonProperties(shapeModel);
        this.addStrokeProperties(shapeModel, 0);
        this.addFillProperties(shapeModel, 0);
        this.setCorrectSize(shapeModel, parseFloat(item.width), parseFloat(item.height), topLeftPosition, item);

        shapeModel.horizontalFlip = 1;
        shapeModel.verticalFlip = 1;
        shapeModel.isNewFlip = true;

        if (item.contentItemId > 0)
            shapeModel.contentItemId = item.contentItemId;

        shapeModel.xlinkHref = item.href;
        shapeModel.logicalName = item.logicalName;

        shapeModel.theme = item.theme;

        if (topLeftPosition)
            shapeModel.hasTopLeftPosition = true;

        dispatch(createShape(shapeModel));
        this.updateThumbnail(dispatch);
        audioEditorUtils.addActionToHistory();
        dispatch(selectShape(shapeModel.uuid));
        saveManager.isContentChanged(true);
    },
    setCorrectSize(shapeModel, originalWidth, originalHeight, topLeftPosition, item) {
        var size = properties.IMAGE_SIZE;
        const useOriginalSize = true;

        thumbnailGenerator.checkImageOriginalSize = true;

        if (item && item.logicalTitle && item.logicalTitle.indexOf('props1.naturfag_5_7') != -1) {
            size = 50;
            thumbnailGenerator.checkImageOriginalSize = false;
        }

        if (originalWidth > 0 && originalHeight > 0) {

            if (useOriginalSize == true) {
                shapeModel.width = originalWidth;
                shapeModel.height = originalHeight;
                shapeModel.isNewImage = false; // don't load image with ThumbnailGenerator
            } else {
                if (originalWidth > originalHeight) {
                    shapeModel.width = size;
                    shapeModel.height = size * originalHeight / originalWidth;
                } else {
                    shapeModel.height = size;
                    shapeModel.width = size * originalWidth / originalHeight;
                }
            }
        } else {
            shapeModel.width = size;
            shapeModel.height = size;
        }

        if (shapeModel.mindMapProperty) {
            shapeModel.width = properties.MIND_MAP_NODE_SIZE;
            shapeModel.height = properties.MIND_MAP_NODE_SIZE;
        }

        if (topLeftPosition) {
            shapeModel.x = topLeftPosition.x;
            shapeModel.y = topLeftPosition.y;
        } else {
            shapeModel.x = configuration.CANVAS_WIDTH / 2 - shapeModel.width / 2;
            shapeModel.y = configuration.CANVAS_HEIGHT / 2 - shapeModel.height / 2;
        }
    },
    createCharacter(dispatch, item, topLeftPosition, mindMapProperty) {
        let shapeModel = {};

        this.dragDropTopLeftPosition = topLeftPosition;
        shapeModel.type = shapeTypes.CHARACTER;
        shapeModel.isNewImage = true;

        if (mindMapProperty)
            shapeModel.mindMapProperty = mindMapProperty;

        this.addCommonProperties(shapeModel);
        this.addStrokeProperties(shapeModel, 0);
        this.addFillProperties(shapeModel, 0);
        this.setCorrectSize(shapeModel, parseFloat(item.width), parseFloat(item.height), topLeftPosition, item);

        shapeModel.horizontalFlip = 1;
        shapeModel.verticalFlip = 1;
        shapeModel.isNewFlip = true;

        shapeModel.xlinkHref = item.href;
        shapeModel.logicalName = item.logicalName;
        shapeModel.moods = item.moods;
        shapeModel.frameLabel = item.moods[0];

        shapeModel.theme = item.theme;

        dispatch(createShape(shapeModel));
        this.updateThumbnail(dispatch);
        audioEditorUtils.addActionToHistory();
        dispatch(selectShape(shapeModel.uuid));
        saveManager.isContentChanged(true);
    },
    createBackground() {
        let shapeModel = {};

        shapeModel.type = shapeTypes.BACKGROUND;
        shapeModel.hotSpot = '';
        shapeModel.logicalName = '';
        shapeModel.frameLabel = '';
        shapeModel.xlinkHref = '';
        shapeModel.moods = [];
        shapeModel.stroke = '#000000';
        shapeModel.strokeWidth = 1;
        shapeModel.strokeOpacity = 0;
        shapeModel.fill = '#ffffff';
        shapeModel.fillOpacity = 1;
        shapeModel.opacity = 1;
        shapeModel.uuid = uuid.v4();

        return shapeModel;
    },
    createBackgroundWithVideo(videoModel) {
        let shapeModel = {};

        shapeModel.type = shapeTypes.BACKGROUND;
        shapeModel.hotSpot = '';
        shapeModel.logicalName = videoModel.logicalName;
        shapeModel.frameLabel = '';
        shapeModel.xlinkHref = videoModel.href;
        shapeModel.moods = [];
        shapeModel.stroke = '#000000';
        shapeModel.strokeWidth = 1;
        shapeModel.strokeOpacity = 0;
        shapeModel.fill = '#ffffff';
        shapeModel.fillOpacity = 1;
        shapeModel.uuid = uuid.v4();

        if (videoModel.contentItemId > 0)
            shapeModel.contentItemId = videoModel.contentItemId;

        shapeModel.isVideo = true;
        shapeModel.volume = videoModel.volume;
        shapeModel.inPoint = videoModel.inPoint;
        shapeModel.outPoint = videoModel.outPoint;

        return shapeModel;
    },
    createVideo(dispatch, item, topLeftPosition) {
        let shapeModel = {};

        shapeModel.type = shapeTypes.VIDEO;

        this.addCommonProperties(shapeModel);
        this.addStrokeProperties(shapeModel, 0);

        shapeModel.x = 100;
        shapeModel.y = 100;
        shapeModel.width = 300;
        shapeModel.height = 300 * 9 / 16;

        shapeModel.horizontalFlip = 1;
        shapeModel.verticalFlip = 1;

        if (item.contentItemId > 0)
            shapeModel.contentItemId = item.contentItemId;

        shapeModel.xlinkHref = item.href;
        shapeModel.duration = item.duration;

        if (topLeftPosition)
            shapeModel.hasTopLeftPosition = true;

        shapeModel.title = item.title;

        dispatch(createShape(shapeModel));
        this.updateThumbnail(dispatch);
        audioEditorUtils.addActionToHistory();
        dispatch(selectShape(shapeModel.uuid));
        saveManager.isContentChanged(true);
    },
    dragDropTopLeftPosition: undefined,
    getShapeIndex(uuid) {
        var state = storeManager.getState();
        var shapes;
        var index = 0;

        if (state && state.currentSlide && state.currentSlide.model && state.currentSlide.model.content)
            shapes = state.currentSlide.model.content;

        if (!shapes)
            return 1;

        for (var i=0; i<shapes.length; i++) {
            if (shapes[i].type == shapeTypes.TEXTBOX || shapes[i].type == shapeTypes.TEXTBOXSIMPLE || shapes[i].type == shapeTypes.SPEAK_BUBBLE  || shapes[i].type == shapeTypes.COMPLEX_SPEAK_BUBBLE || shapes[i].type == shapeTypes.SCREAM_BUBBLE || shapes[i].type == shapeTypes.THOUGHT_BUBBLE) {
                index = index + 1;

                if (shapes[i].uuid == uuid)
                    return index;
            }
        }

        return 1;
    }
};

export default createShapes;



/** WEBPACK FOOTER **
 ** ./src/components/utils/CreateShapes.js
 **/